import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  makeStyles,
  Typography,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { getEventActivity } from '@pingum/app-state-management/event/actions';
import { getUserEventActivity } from '@pingum/app-state-management/userEvents/actions';

import HistoryItem from '../../../EventDetailPage/HistoryItem';

// utils
import {
  buildHistoryItemObjects,
  getActivityEvent,
} from '../../../../../utils/eventActivityHelper';

import styles from './styles';

const useStyles = makeStyles(styles);
const defaultPageable = {
  page: 0,
  totalPages: null,
  isFetchingEventActivity: false,
};
const pageSize = 10;

export const RecentOrganizationActivityCardComponent = ({
  onEventClick,
  statuses,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [pageable, setPageable] = useState({
    all: defaultPageable,
    my: defaultPageable,
    showMyRecentEvents: false,
  });
  const [recentEvents, setRecentEvents] = useState([]);
  const [myRecentEvents, setMyRecentEvents] = useState([]);
  const endOfListLoader = useRef(null);

  const {
    event: {
      eventActivityPageable,
      isFetchingEventActivity,
    },
    tenant: { tenantUsers },
    userEvents: {
      userEventActivityPageable,
      isFetchingUserEventActivity,
    },
    user: { user: { user_id: auth0UserId } },
  } = useSelector((state) => state);

  useEffect(() => {
    dispatch(getEventActivity(pageable.all.page, pageSize));
  }, [pageable.all.page]);

  useEffect(() => {
    dispatch(getUserEventActivity(auth0UserId, pageable.my.page, pageSize));
  }, [pageable.my.page]);

  useEffect(() => {
    setRecentEvents([
      ...recentEvents,
      ...eventActivityPageable.content,
    ]);
    setPageable((prevPageable) => ({
      ...prevPageable,
      all: {
        ...prevPageable.all,
        totalPages: eventActivityPageable.totalPages,
        isFetchingEventActivity,
      },
    }));
  }, [eventActivityPageable, isFetchingEventActivity]);

  useEffect(() => {
    setMyRecentEvents([
      ...myRecentEvents,
      ...userEventActivityPageable.content,
    ]);
    setPageable((prevPageable) => ({
      ...prevPageable,
      my: {
        ...prevPageable.my,
        totalPages: userEventActivityPageable.totalPages,
        isFetchingEventActivity: isFetchingUserEventActivity,
      },
    }));
  }, [userEventActivityPageable, isFetchingUserEventActivity]);

  const handleChangeShowMyRecentEvents = () => {
    setPageable((prevPageable) => ({
      ...prevPageable,
      showMyRecentEvents: !prevPageable.showMyRecentEvents,
    }));
  };

  const handleInfiniteScroll = ([target]) => {
    if (target.isIntersecting) {
      setPageable((prevPageable) => { // have to use callback otherwise page is always initial state
        const currentPageable = prevPageable.showMyRecentEvents
          ? prevPageable.my
          : prevPageable.all;
        const newPageableKey = prevPageable.showMyRecentEvents
          ? 'my'
          : 'all';
        if (currentPageable.page < currentPageable.totalPages && !currentPageable.isFetchingEventActivity) {
          return {
            ...prevPageable,
            [newPageableKey]: {
              ...currentPageable,
              page: currentPageable.page + 1,
            },
          };
        }
        return prevPageable;
      });
    }
  };

  const observer = new IntersectionObserver(handleInfiniteScroll, {
    root: null,
    rootMargin: '40px',
    threshold: 1.0,
  });

  useEffect(() => {
    observer.observe(endOfListLoader.current);
  }, [endOfListLoader]);

  return (
    <>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>Recent Activity</Typography>
        <FormControlLabel
          control={<Checkbox checked={pageable.showMyRecentEvents} onChange={handleChangeShowMyRecentEvents} name="checkedA" />}
          label="Only show my activity"
        />
      </div>
      <Grid container>
        {
          buildHistoryItemObjects(
            pageable.showMyRecentEvents
              ? myRecentEvents
              : recentEvents,
            statuses,
          ).map((historyItem) => (
            <Grid item xs={12} className={classes.historyItemContainer}>
              {
                getActivityEvent({
                  activity: historyItem,
                  onEventClick,
                  statuses,
                  childComponent: (
                    <HistoryItem
                      key={`history-item-${historyItem.type}-${historyItem.id}`}
                      item={historyItem}
                      tenantUsers={tenantUsers}
                      hideActions
                      event={JSON.parse(historyItem.event)}
                    />
                  ),
                  classes,
                })
              }
            </Grid>
          ))
        }
      </Grid>
      <div ref={endOfListLoader} className={classes.loadingContainer}>
        {
          isFetchingEventActivity
            && <CircularProgress size={60} />
        }
      </div>
    </>
  );
};

RecentOrganizationActivityCardComponent.propTypes = {
  onEventClick: PropTypes.func.isRequired,
  statuses: PropTypes.array.isRequired,
};

RecentOrganizationActivityCardComponent.defaultProps = { };

export default RecentOrganizationActivityCardComponent;
